/**
 * @tanstack/react-query hooks for the Leave Management module + the
 * cross-module notification bell (Phase 4.3 / 4.5-ui). Thin wrappers
 * over lib/api/leave.ts so pages stay declarative and
 * caching/invalidation lives in one place.
 *
 * Query-key convention mirrors use-posh.ts:
 *   ["leave", <area>, ...params]   — flush whole module with ["leave"]
 *   ["notifications", <area>]      — bell is its OWN root (cross-module,
 *                                    survives a leave-cache flush)
 *
 * The shared QueryClient (components/providers.tsx) sets retry: 1,
 * refetchOnWindowFocus: false, staleTime: 30s. The leave-type catalogue
 * changes rarely → longer staleTime; requests/balance/notifications use
 * the default. The unread-count poll is the only refetchInterval (the
 * bell badge must stay roughly live without a websocket).
 *
 * Wave 2: a single request is read from GET /leave/requests/{id}
 * (hooks/use-leave-request.ts → useLeaveRequest); deriving it from the
 * list cache (useLeaveRequestFromList, kept for compatibility) showed
 * "not found" to approvers the list did not include. There is no
 * leave-timeline table; the request's own lifecycle fields are the whole
 * "timeline".
 */

import { useEffect, useRef } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { leaveApi } from "@/lib/api";
import { invalidateApprovalsSummary } from "@/lib/approvals-invalidate";
import type {
  CreateLeaveRequestRequest,
  CreateLeaveTypeRequest,
  LeaveDecisionRequest,
  LeaveRequest,
  LeaveSettings,
  UpdateLeaveSettingsRequest,
  UpdateLeaveTypeRequest,
} from "@/types/leave";
import type { GrantCompOffRequest } from "@/types/comp-off";
import {
  changedLeaveSettings,
  isEmptySettingsPatch,
} from "@/lib/leave-settings-diff";

const CATALOGUE_STALE = 5 * 60 * 1000; // 5 min — leave-type catalogue
// Bell badge refresh cadence. 60s is a sane no-websocket compromise:
// frequent enough that a leave decision / POSH deadline surfaces within
// a minute, infrequent enough to not hammer the API (one tiny COUNT(*)
// query). Only polls while the tab is visible (react-query default
// refetchIntervalInBackground=false) so a backgrounded PWA is quiet.
const UNREAD_POLL_MS = 60_000;

export const leaveKeys = {
  all: ["leave"] as const,
  types: () => [...leaveKeys.all, "types"] as const,
  // `view` discriminates the server-side row scope: "excl-self" (approver
  // queue minus the caller's own rows), "mine" (only the caller's own
  // rows) or the default role scope.
  requests: (status?: string, view?: "excl-self" | "mine") =>
    [...leaveKeys.all, "requests", status ?? "all", view ?? "role"] as const,
  // year undefined = "the server's current leave-year" (or the leave-year
  // containing `date` when given) — never a client-computed calendar year.
  balance: (userId?: number, year?: number, date?: string) =>
    [
      ...leaveKeys.all,
      "balance",
      userId ?? "self",
      year ?? (date ? `on:${date}` : "current"),
    ] as const,
  settings: () => [...leaveKeys.all, "settings"] as const,
  compOff: (userId?: number, status?: string) =>
    [...leaveKeys.all, "comp-off", userId ?? "self", status ?? "all"] as const,
  compOffBalance: (userId?: number) =>
    [...leaveKeys.all, "comp-off-balance", userId ?? "self"] as const,
};

export const notificationKeys = {
  all: ["notifications"] as const,
  list: (limit?: number) =>
    [...notificationKeys.all, "list", limit ?? 30] as const,
  unread: () => [...notificationKeys.all, "unread"] as const,
};

// ---------------------------------------------------------------------------
// Leave types
// ---------------------------------------------------------------------------

export function useLeaveTypes() {
  return useQuery({
    queryKey: leaveKeys.types(),
    queryFn: ({ signal }) => leaveApi.listLeaveTypes(signal),
    staleTime: CATALOGUE_STALE,
  });
}

export function useCreateLeaveType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLeaveTypeRequest) =>
      leaveApi.createLeaveType(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leaveKeys.types() });
    },
  });
}

export function useUpdateLeaveType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateLeaveTypeRequest }) =>
      leaveApi.updateLeaveType(vars.id, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leaveKeys.types() });
    },
  });
}

// ---------------------------------------------------------------------------
// Leave requests
// ---------------------------------------------------------------------------

export function useLeaveRequests(
  status?: string,
  opts?: { excludeSelf?: boolean; mine?: boolean },
) {
  const view = opts?.excludeSelf ? "excl-self" : opts?.mine ? "mine" : undefined;
  return useQuery({
    queryKey: leaveKeys.requests(status, view),
    queryFn: ({ signal }) =>
      leaveApi.listLeaveRequests(
        {
          ...(status ? { status } : {}),
          ...(opts?.excludeSelf ? { exclude_self: true } : {}),
          ...(opts?.mine ? { mine: true } : {}),
        },
        signal,
      ),
  });
}

/**
 * One employee's requests (employee-detail Leave tab, 2026-08-07).
 * `?user_id=` is narrowing-only server-side: applied after the
 * role-derived SQL scope, so HR narrows the tenant slice to one
 * employee while a non-privileged caller passing someone else's id
 * simply receives an empty list. Its own key under the ["leave"] root
 * so leave mutations flush it with everything else.
 */
export function useLeaveRequestsForUser(userId: number, enabled = true) {
  return useQuery({
    queryKey: [...leaveKeys.all, "requests", "by-user", userId] as const,
    queryFn: ({ signal }) =>
      leaveApi.listLeaveRequests({ user_id: userId }, signal),
    enabled,
    retry: false,
  });
}

/**
 * @deprecated Use useLeaveRequest (hooks/use-leave-request.ts) — the
 * direct GET /leave/requests/{id} (wave 2).
 *
 * Derive a single request from the (already role-scoped) list query.
 *
 * There is NO GET /leave/requests/{id} endpoint and no timeline table
 * (deliberate — confirmed in the 4.2 backend audit). The detail page
 * therefore loads the unfiltered list (status omitted, so it includes
 * every state the caller's SQL scope allows) and finds the row by id.
 * If the list is empty/loading the page shows its own skeleton; a
 * genuinely-not-found id renders the "not found" state. We key off the
 * "all" requests query so a fresh page load (or an invalidation after a
 * decision) refetches and the derived row stays consistent.
 */
export function useLeaveRequestFromList(id: number): {
  request: LeaveRequest | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isFetched: boolean;
  isError: boolean;
  error: unknown;
} {
  const q = useLeaveRequests();
  const request = q.data?.leave_requests.find((r) => r.id === id);
  return {
    request,
    isLoading: q.isLoading,
    // Surface fetch state so the detail page can distinguish "still
    // resolving the shared list" from "genuinely absent". On a cache
    // hit isLoading is already false even though a background refetch
    // may be in flight, which caused a "Request not found" flash before
    // the row arrived (P1-5).
    isFetching: q.isFetching,
    isFetched: q.isFetched,
    isError: q.isError,
    error: q.error,
  };
}

/**
 * Invalidate every list+balance query after a request mutation. A new
 * or decided request changes the (status-filtered) lists AND the
 * pending/used balance ledger, so we flush the whole leave root rather
 * than guess which status buckets/years moved.
 */
function invalidateLeaveAfterMutation(
  qc: ReturnType<typeof useQueryClient>,
) {
  void qc.invalidateQueries({ queryKey: leaveKeys.all });
  // A decision/cancel also generates an in-app notification for the
  // counterparty; refresh the bell so it surfaces promptly.
  void qc.invalidateQueries({ queryKey: notificationKeys.all });
  // The Inbox / tab badges count pending leave (phantom-count fix).
  invalidateApprovalsSummary(qc);
}

export function useCreateLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLeaveRequestRequest) =>
      leaveApi.createLeaveRequest(body),
    onSuccess: () => invalidateLeaveAfterMutation(qc),
  });
}

export function useApproveLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body?: LeaveDecisionRequest }) =>
      leaveApi.approveLeaveRequest(vars.id, vars.body),
    onSuccess: () => invalidateLeaveAfterMutation(qc),
  });
}

export function useRejectLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body?: LeaveDecisionRequest }) =>
      leaveApi.rejectLeaveRequest(vars.id, vars.body),
    onSuccess: () => invalidateLeaveAfterMutation(qc),
  });
}

export function useCancelLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => leaveApi.cancelLeaveRequest(id),
    onSuccess: () => invalidateLeaveAfterMutation(qc),
  });
}

// ---------------------------------------------------------------------------
// Leave balances
// ---------------------------------------------------------------------------

/**
 * Leave balances for one leave-year. Pass `year` only for an explicit
 * historical/future leave-year; omit it to get the tenant's CURRENT
 * leave-year, or pass `date` (YYYY-MM-DD) for the leave-year containing
 * that date — both resolved server-side under the tenant's anchor
 * (an April-anchored tenant's Feb 2027 is leave-year 2026, "FY 2026-27").
 * The response's `leave_year.label` is the display name.
 */
export function useLeaveBalance(
  userId?: number,
  year?: number,
  date?: string,
  enabled = true,
) {
  return useQuery({
    enabled,
    queryKey: leaveKeys.balance(userId, year, date),
    queryFn: ({ signal }) =>
      leaveApi.getLeaveBalance(
        {
          ...(userId ? { user_id: userId } : {}),
          ...(year ? { year } : {}),
          ...(!year && date ? { date } : {}),
        },
        signal,
      ),
  });
}

// ---------------------------------------------------------------------------
// Leave settings (063: leave-year anchor + comp-off / probation config)
// ---------------------------------------------------------------------------

export function useLeaveSettings() {
  return useQuery({
    queryKey: leaveKeys.settings(),
    queryFn: ({ signal }) => leaveApi.getLeaveSettings(signal),
    staleTime: CATALOGUE_STALE,
  });
}

export function useUpdateLeaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    // PATCH only what differs from the last-known server state (QA 2026-10
    // leave #14). The caller may pass every field it shows; an untouched
    // one is never sent, so it can't overwrite a newer value saved
    // elsewhere — the weekly off is shared with Settings → Attendance.
    mutationFn: async (body: UpdateLeaveSettingsRequest) => {
      const loaded = qc.getQueryData<LeaveSettings>(leaveKeys.settings());
      const patch = changedLeaveSettings(loaded, body);
      if (loaded && isEmptySettingsPatch(patch)) return loaded;
      return leaveApi.updateLeaveSettings(patch);
    },
    onSuccess: () => {
      // The anchor decides which leave-year is "current", so every cached
      // balance view (keyed "current") is stale too — flush the module.
      void qc.invalidateQueries({ queryKey: leaveKeys.all });
      // ...and the weekly off is the attendance setting too.
      void qc.invalidateQueries({ queryKey: ["attendance", "settings"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Comp-off (063)
// ---------------------------------------------------------------------------

export function useCompOff(userId?: number, status?: string) {
  return useQuery({
    queryKey: leaveKeys.compOff(userId, status),
    queryFn: ({ signal }) =>
      leaveApi.listCompOff(
        {
          ...(userId ? { user_id: userId } : {}),
          ...(status ? { status } : {}),
        },
        signal,
      ),
  });
}

export function useCompOffBalance(userId?: number) {
  return useQuery({
    queryKey: leaveKeys.compOffBalance(userId),
    queryFn: ({ signal }) =>
      leaveApi.getCompOffBalance(userId ? { user_id: userId } : undefined, signal),
  });
}

function invalidateCompOff(qc: ReturnType<typeof useQueryClient>) {
  // A grant/revoke moves both the list and the available-balance; flush the
  // whole leave root (cheap, and a comp-off change can affect the balance card).
  void qc.invalidateQueries({ queryKey: leaveKeys.all });
  void qc.invalidateQueries({ queryKey: notificationKeys.all });
}

export function useGrantCompOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GrantCompOffRequest) => leaveApi.grantCompOff(body),
    onSuccess: () => invalidateCompOff(qc),
  });
}

export function useRevokeCompOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => leaveApi.revokeCompOff(id),
    onSuccess: () => invalidateCompOff(qc),
  });
}

// ---------------------------------------------------------------------------
// Notifications — the cross-module bell
// ---------------------------------------------------------------------------

/**
 * Unread badge count. Polls on UNREAD_POLL_MS so the bell stays roughly
 * live without a websocket; only while the tab is foregrounded
 * (refetchIntervalInBackground defaults to false). staleTime 0 so the
 * count is always re-fetched on the interval / focusless re-mount.
 */
export function useUnreadCount() {
  const qc = useQueryClient();
  const prevUnreadRef = useRef<number | undefined>(undefined);
  const q = useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: ({ signal }) => leaveApi.getUnreadCount(signal),
    refetchInterval: UNREAD_POLL_MS,
    staleTime: 0,
  });

  // A notification means "something changed elsewhere" (a decision on my
  // request, a POSH deadline, …). The persisted query cache + 5-min
  // staleTime otherwise keeps showing the pre-decision snapshot — the
  // requester reads "your leave was approved" while the detail page still
  // says Pending. Flush everything except the bell's own keys when
  //   (a) the count RISES mid-session (a notification just arrived), or
  //   (b) the FIRST reading after boot is > 0 (the change happened while
  //       we were away and localStorage rehydrated the stale snapshot).
  const unread = q.data?.unread;
  useEffect(() => {
    if (unread === undefined) return;
    const prev = prevUnreadRef.current;
    prevUnreadRef.current = unread;
    const bootWithPending = prev === undefined && unread > 0;
    const newArrival = prev !== undefined && unread > prev;
    if (bootWithPending || newArrival) {
      void qc.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] !== notificationKeys.all[0],
      });
    }
  }, [unread, qc]);

  return q;
}

/**
 * The dropdown feed. `enabled` lets the bell defer the (heavier) list
 * fetch until the panel is actually opened — the badge poll alone keeps
 * the count fresh in the meantime.
 */
export function useNotifications(limit = 15, enabled = true) {
  return useQuery({
    queryKey: notificationKeys.list(limit),
    queryFn: ({ signal }) => leaveApi.listNotifications({ limit }, signal),
    enabled,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => leaveApi.markNotificationRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => leaveApi.markAllNotificationsRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
